import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { firmHasPartialPayments } from '@/config/firm-features';
import {
  getStatusChoices,
  getStatusColor,
  getDocumentTypeName,
  getDocumentEndpoint,
  formatStatusLabel,
  type DocumentType,
} from '@/utils/document';

interface StatusDropdownProps {
  documentType: DocumentType;
  documentId: number;
  documentNumber: string;
  currentStatus: string;
  onStatusChanged: () => void;
  /** Needed for the partly-paid flow (validation and prefill). */
  totalAmount?: number;
  amountPaid?: number;
}

export function StatusDropdown({
  documentType,
  documentId,
  documentNumber,
  currentStatus,
  onStatusChanged,
  totalAmount,
  amountPaid,
}: StatusDropdownProps) {
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paidInput, setPaidInput] = useState('');

  const partialPayments =
    documentType === 'invoice' && firmHasPartialPayments(user?.firms);

  const submitStatusChange = async (
    newStatus: string,
    extra?: Record<string, unknown>,
  ) => {
    try {
      setIsUpdating(true);

      // Use the dedicated update_status endpoint so side effects
      // (e.g. releasing timesheets on cancel) run reliably and
      // errors are surfaced to the user instead of silently logged.
      await api.post(`${getDocumentEndpoint(documentId)}update_status/`, {
        status: newStatus,
        ...extra,
      });

      toast({
        title: 'Status Updated',
        description: `${getDocumentTypeName(documentType)} ${documentNumber} status changed to ${formatStatusLabel(newStatus)}`,
      });

      onStatusChanged();
      return true;
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to update status',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus && newStatus !== 'partly_paid') return;

    if (newStatus === 'partly_paid') {
      setPaidInput(amountPaid ? String(amountPaid) : '');
      setPaidDialogOpen(true);
      return;
    }
    await submitStatusChange(newStatus);
  };

  const confirmPartlyPaid = async () => {
    const amount = Number(paidInput.replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0) {
      toast({
        title: 'Invalid amount',
        description: 'Enter the amount received as a number.',
        variant: 'destructive',
      });
      return;
    }
    if (totalAmount !== undefined && amount > totalAmount) {
      toast({
        title: 'Invalid amount',
        description: 'The amount paid cannot exceed the invoice total.',
        variant: 'destructive',
      });
      return;
    }
    const ok = await submitStatusChange('partly_paid', { amount_paid: amount });
    if (ok) setPaidDialogOpen(false);
  };

  const statusChoices = [
    ...getStatusChoices(documentType),
    ...(partialPayments ? [{ value: 'partly_paid', label: 'Partly Paid' }] : []),
  ];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={isUpdating} className="h-auto p-0">
            <Badge className={getStatusColor(currentStatus)} variant="outline">
              {formatStatusLabel(currentStatus)}
              <ChevronDown className="h-3 w-3 ml-1" />
            </Badge>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {statusChoices.map((choice) => (
            <DropdownMenuItem
              key={choice.value}
              onClick={() => handleStatusChange(choice.value)}
              disabled={choice.value === currentStatus && choice.value !== 'partly_paid'}
            >
              <Badge className={getStatusColor(choice.value)} variant="outline">
                {choice.label}
              </Badge>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {partialPayments && (
        <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Mark {documentNumber} partly paid</DialogTitle>
              <DialogDescription>
                Enter the amount received so far
                {totalAmount !== undefined &&
                  ` (invoice total ${totalAmount.toLocaleString('nl-BE', {
                    style: 'currency',
                    currency: 'EUR',
                  })})`}
                .
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="amount-paid">Amount paid</Label>
              <Input
                id="amount-paid"
                type="number"
                min="0"
                step="0.01"
                max={totalAmount}
                value={paidInput}
                onChange={(e) => setPaidInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmPartlyPaid();
                  }
                }}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaidDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmPartlyPaid} disabled={isUpdating}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
